import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Repeat, Target } from 'lucide-react'
import { formatAccountTypeLabel, type AccountSummary } from '@/api/accounts'
import { getEarmarks, listGoals } from '@/api/goals'
import { listRecurring } from '@/api/recurring'
import { listValuations } from '@/api/valuations'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { formatDate, formatShortDate } from '@/lib/formatDate'
import { FREQUENCY_LABELS } from '@/lib/recurring'
import { ROUTES } from '@/routes/routes'
import { cn } from '@/lib/utils'

export function SectionHeader({
  title,
  count,
  right,
}: {
  title: string
  count?: number
  right?: React.ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-1.5 pt-5 pb-2">
      <h2 className="text-[11px] font-semibold tracking-[0.08em] text-[var(--fl-text-3)] uppercase">
        {title}
        {count !== undefined && ` · ${count}`}
      </h2>
      {right}
    </div>
  )
}

export function SectionCard({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl bg-[var(--fl-surface)] shadow-[var(--card-shadow-sm)]',
        className
      )}
    >
      {children}
    </div>
  )
}

const ROW = 'flex items-center gap-3 border-b border-[var(--fl-divider)] px-4 py-3 last:border-b-0'
const TILE = 'grid h-9 w-9 shrink-0 place-items-center rounded-xl'

/** Goals with money set aside in this account; hidden when there are none. */
export function AccountGoals({ accountId }: { accountId: string }) {
  const format = useListAmountFormat()
  const { data: earmarks } = useQuery({
    queryKey: ['earmarks', accountId],
    queryFn: () => getEarmarks(accountId),
  })
  const { data: goalsData } = useQuery({ queryKey: ['goals'], queryFn: () => listGoals() })
  const goals = earmarks?.goals ?? []
  if (goals.length === 0) return null

  return (
    <section aria-label="Set aside for goals">
      <SectionHeader
        title="Set aside for goals"
        count={goals.length}
        right={
          <span className="text-[13px] font-medium tabular-nums text-[var(--fl-text-2)]">
            {format(earmarks!.earmarked)}
          </span>
        }
      />
      <SectionCard>
        {goals.map((g) => {
          const goal = [
            ...(goalsData?.active ?? []),
            ...(goalsData?.paused ?? []),
            ...(goalsData?.completed ?? []),
          ].find((x) => x.id === g.id)
          const progress =
            goal && goal.targetAmount > 0
              ? Math.min(1, goal.currentAmount / goal.targetAmount)
              : null
          return (
            <Link
              key={g.id}
              to={ROUTES.APP.GOAL_DETAIL(g.id)}
              className={cn(ROW, 'hover:bg-[var(--fl-surface-hover)]')}
            >
              <span className={cn(TILE, 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning)]')}>
                <Target size={16} aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-[var(--fl-heading)]">
                  {g.name}
                </span>
                {progress !== null && goal && (
                  <>
                    <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-[var(--fl-surface-subtle)]">
                      <span
                        className="block h-full rounded-full bg-[var(--fl-primary)]"
                        style={{ width: `${Math.round(progress * 100)}%` }}
                      />
                    </span>
                    <span className="mt-1 block text-xs text-[var(--fl-text-3)]">
                      {format(goal.currentAmount)} of {format(goal.targetAmount)}
                    </span>
                  </>
                )}
              </span>
              <span className="font-serif text-base font-semibold tabular-nums text-[var(--fl-heading)]">
                {format(g.earmarkedAmount)}
              </span>
            </Link>
          )
        })}
      </SectionCard>
    </section>
  )
}

/** Recurring items paid from (or, for an investment, into) this account. */
export function AccountRepeats({ account, onAdd }: { account: AccountSummary; onAdd: () => void }) {
  const format = useListAmountFormat()
  const isReadOnly = useIsReadOnly()
  const { data } = useQuery({ queryKey: ['recurring'], queryFn: () => listRecurring() })
  const items = data?.items.filter((r) => r.financialAccountId === account.id) ?? []
  const title =
    account.type === 'INVESTMENT' ? 'Repeats into this account' : 'Repeats from this account'

  return (
    <section aria-label={title}>
      <SectionHeader
        title={title}
        count={items.length}
        right={
          !isReadOnly && (
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-1 text-[13px] font-medium text-[var(--fl-primary)] hover:underline"
            >
              <Plus size={13} aria-hidden="true" /> Add
            </button>
          )
        }
      />
      <SectionCard>
        {items.length === 0 ? (
          <p className="px-4 py-3.5 text-sm text-[var(--fl-text-3)]">
            Nothing repeats {account.type === 'INVESTMENT' ? 'into' : 'from'} this account yet.
          </p>
        ) : (
          items.map((item) => (
            <div key={item.id} className={ROW}>
              <span
                className={cn(TILE, 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]')}
              >
                <Repeat size={15} aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-[var(--fl-heading)]">
                  {item.name}
                </span>
                <span className="block text-xs text-[var(--fl-text-3)]">
                  {FREQUENCY_LABELS[item.frequency]} · next {formatShortDate(item.nextDueDate)}
                </span>
              </span>
              <span className="font-serif text-base font-semibold tabular-nums text-[var(--fl-heading)]">
                {format(item.amount)}
              </span>
            </div>
          ))
        )}
      </SectionCard>
      <Link
        to={ROUTES.APP.RECURRING}
        className="mt-2 inline-block px-1.5 text-xs text-[var(--fl-text-3)] hover:text-[var(--fl-primary)]"
      >
        Manage in Recurring →
      </Link>
    </section>
  )
}

const VALUE_UPDATES_SHOWN = 3

/** Recorded values, newest first, each with its change from the one before. */
export function ValueUpdates({ accountId }: { accountId: string }) {
  const format = useListAmountFormat()
  const [showAll, setShowAll] = useState(false)
  const { data: valuations } = useQuery({
    queryKey: ['valuations', accountId],
    queryFn: () => listValuations(accountId),
  })
  if (!valuations || valuations.length === 0) return null
  const shown = showAll ? valuations : valuations.slice(0, VALUE_UPDATES_SHOWN)

  return (
    <section aria-label="Value updates">
      <SectionHeader
        title="Value updates"
        count={valuations.length}
        right={
          valuations.length > VALUE_UPDATES_SHOWN && (
            <button
              type="button"
              onClick={() => setShowAll((s) => !s)}
              className="text-[13px] font-medium text-[var(--fl-primary)] hover:underline"
            >
              {showAll ? 'Show fewer' : 'See all'}
            </button>
          )
        }
      />
      <SectionCard>
        {shown.map((v, i) => {
          const previous = valuations[i + 1]
          const change = previous ? v.currentValue - previous.currentValue : null
          return (
            <div key={v.id} className={ROW}>
              <span className="flex-1 font-medium text-[var(--fl-heading)]">
                {formatDate(v.asOfDate)}
              </span>
              <span className="text-right">
                <span className="block font-serif text-base font-semibold tabular-nums text-[var(--fl-heading)]">
                  {format(v.currentValue)}
                </span>
                {change !== null && change !== 0 && (
                  <span
                    className={cn(
                      'block text-[11px] tabular-nums',
                      change > 0 ? 'text-[var(--fl-income)]' : 'text-[var(--fl-expense)]'
                    )}
                  >
                    {change > 0 ? '+' : '−'}
                    {format(Math.abs(change))}
                  </span>
                )}
              </span>
            </div>
          )
        })}
      </SectionCard>
    </section>
  )
}

export function AboutAccount({
  account,
  ownerNames,
}: {
  account: AccountSummary
  ownerNames: string[]
}) {
  const format = useListAmountFormat()
  const rows: [string, string][] = []
  if (ownerNames.length > 0) rows.push(['Owners', ownerNames.join(', ')])
  rows.push([
    account.type === 'PROPERTY' ? 'Bought for' : 'Started with',
    `${format(Math.abs(account.openingBalance))} · ${formatDate(account.createdAt)}`,
  ])
  rows.push([
    'Type',
    [
      formatAccountTypeLabel(account.type),
      account.subType && formatAccountTypeLabel(account.subType),
    ]
      .filter(Boolean)
      .join(' · ')
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase()),
  ])

  return (
    <section aria-label="About this account">
      <SectionHeader title="About this account" />
      <SectionCard>
        <dl>
          {rows.map(([label, value]) => (
            <div
              key={label}
              className="flex justify-between gap-4 border-b border-[var(--fl-divider)] px-4 py-3 text-[13px] last:border-b-0"
            >
              <dt className="text-[var(--fl-text-3)]">{label}</dt>
              <dd className="text-right font-medium text-[var(--fl-text-1)]">{value}</dd>
            </div>
          ))}
        </dl>
      </SectionCard>
    </section>
  )
}
